/** D-871 — one linked Google account; `email` is null until Google has reported it. */
export type GoogleLinkEntry = { id: string; email: string | null; linkedAt: string };
export type GoogleLinkState = {
  phase: 'checking' | 'ready' | 'code' | 'choose' | 'linked' | 'unknown';
  busy: boolean;
  phoneHint: string;
  error: boolean;
  linkedCount: number;
  /** Empty when the server is older than D-871; the screen then shows the count only. */
  links: GoogleLinkEntry[];
  /** The link whose removal the current SMS step will confirm, if any. */
  removing: string | null;
  /** The last finished action, for a short notice. */
  outcome: 'removed' | 'lastMethod' | null;
};
type Dependencies = {
  getGoogleSignInStatus: (session: string) => Promise<{ linked: boolean; linkedAt: string | null; linkedCount?: number; links?: unknown }>;
  startGoogleReauthentication: (session: string) => Promise<{ attempt: string; expiresAt: string; phoneHint: string }>;
  completeGoogleReauthentication: (session: string, attempt: string, code: string) => Promise<{ token: string }>;
  link: (proof: string, signal: AbortSignal) => Promise<{ linked: true } | null>;
  unlinkGoogle: (proof: string, linkId: string) => Promise<{ unlinked: true }>;
};
export const initialGoogleLinkState: GoogleLinkState = {
  phase: 'checking', busy: false, phoneHint: '', error: false, linkedCount: 0, links: [], removing: null, outcome: null,
};

const LINK_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Reads the list defensively; anything malformed is treated as an older server's answer. */
export function parseGoogleLinks(value: unknown): GoogleLinkEntry[] {
  if (!Array.isArray(value)) return [];
  const links = value.filter((l): l is GoogleLinkEntry => !!l && typeof l === 'object'
    && typeof l.id === 'string' && LINK_ID.test(l.id)
    && (l.email === null || (typeof l.email === 'string' && l.email.length <= 320))
    && typeof l.linkedAt === 'string' && Number.isFinite(Date.parse(l.linkedAt)));
  return links.length === value.length ? links.map(({ id, email, linkedAt }) => ({ id, email, linkedAt })) : [];
}

// Credentials stay in this screen-owned closure, never storage or app session.
export function createGoogleLinkController(session: string, api: Dependencies, changed: (state: GoogleLinkState) => void) {
  let state = initialGoogleLinkState;
  let attempt: string | undefined;
  let proof: string | undefined;
  let closed = false;
  const lifetime = new AbortController();
  const publish = (patch: Partial<GoogleLinkState>) => {
    if (closed) return;
    state = { ...state, ...patch };
    changed(state);
  };
  const clear = () => { attempt = undefined; proof = undefined; };
  let removing: string | null = null;
  const restingPhase = () => state.linkedCount > 0 ? 'linked' as const : 'ready' as const;
  const status = async (outcome?: 'completed' | 'uncertain') => {
    const result = await api.getGoogleSignInStatus(session);
    if (!result || typeof result.linked !== 'boolean') throw new Error('invalid_status');
    // Older servers return no count. Never infer a successful *new* link from
    // a pre-existing one when the POST result is uncertain.
    const linkedCount = result.linkedCount === undefined ? (result.linked ? 1 : 0) : result.linkedCount;
    if (!Number.isSafeInteger(linkedCount) || linkedCount < 0 || (linkedCount > 0) !== result.linked) throw new Error('invalid_status');
    const links = parseGoogleLinks(result.links);
    publish({ phase: result.linked ? 'linked' : 'ready', linkedCount, phoneHint: '',
      links: links.length === linkedCount ? links : [], removing: null,
      error: outcome === 'uncertain' || (outcome === 'completed' && !result.linked) });
  };
  async function run(phases: GoogleLinkState['phase'][], work: () => Promise<void>) {
    if (closed || state.busy || !phases.includes(state.phase)) return;
    publish({ busy: true, error: false });
    try { await work(); } finally { publish({ busy: false }); }
  }
  // Sends one SMS code to the number on file. Used for adding and for removing.
  async function sendCode() {
    try {
      const result = await api.startGoogleReauthentication(session);
      if (closed) return;
      if (!result || !/^[A-Za-z0-9_-]{43}$/.test(result.attempt)
        || !Number.isFinite(Date.parse(result.expiresAt)) || typeof result.phoneHint !== 'string'
        || !/^•••• \d{2}$/.test(result.phoneHint)) throw new Error('invalid_attempt');
      attempt = result.attempt;
      publish({ phase: 'code', phoneHint: result.phoneHint });
    } catch { removing = null; publish({ phase: restingPhase(), removing: null, error: true }); }
  }
  return {
    refresh: () => run(['checking', 'unknown', 'ready', 'linked'], async () => {
      clear();
      try { await status(); } catch { publish({ phase: 'unknown', error: true }); }
    }),
    send: () => run(['ready', 'linked'], async () => {
      clear();
      removing = null;
      publish({ removing: null, outcome: null });
      await sendCode();
    }),
    /** D-871 — removing a link starts the same SMS step; the code then confirms the removal. */
    remove: (linkId: string) => run(['linked'], async () => {
      clear();
      if (!state.links.some((l) => l.id === linkId)) { publish({ error: true }); return; }
      removing = linkId;
      publish({ removing: linkId, outcome: null });
      await sendCode();
    }),
    verify: (code: string) => run(['code'], async () => {
      if (!/^\d{4,10}$/.test(code) || !attempt) { publish({ error: true }); return; }
      const current = attempt;
      attempt = undefined; // one check, including network/server failure
      try {
        const result = await api.completeGoogleReauthentication(session, current, code);
        if (closed) return;
        if (!result || typeof result.token !== 'string' || !result.token || result.token.length > 16_384) throw new Error('invalid_proof');
        proof = result.token;
      } catch { clear(); removing = null; publish({ phase: restingPhase(), phoneHint: '', removing: null, error: true }); return; }
      if (!removing) { publish({ phase: 'choose', phoneHint: '' }); return; }
      const target = removing;
      const usedProof = proof;
      clear();
      removing = null;
      let outcome: GoogleLinkState['outcome'] = null;
      try {
        await api.unlinkGoogle(usedProof, target);
        outcome = 'removed';
      } catch (error) {
        if ((error as { code?: unknown; message?: unknown })?.message === 'google_last_sign_in_method') outcome = 'lastMethod';
      }
      if (closed) return;
      // The status endpoint is authoritative, whether the removal answered or not.
      try {
        await status();
        publish({ outcome, error: outcome === null });
      } catch { publish({ phase: 'unknown', removing: null, error: true }); }
    }),
    choose: () => run(['choose'], async () => {
      if (!proof) { publish({ phase: restingPhase(), error: true }); return; }
      try {
        const result = await api.link(proof, lifetime.signal);
        if (closed || result === null) return; // explicit cancellation can retry while proof is valid
        clear();
        // Reconcile even after success. The status endpoint is authoritative.
        try { await status('completed'); } catch { publish({ phase: 'unknown', error: true }); }
      } catch {
        clear();
        if (closed) return;
        // A POST may have succeeded before its response was lost. Never show a
        // false disconnected state or automatically send another SMS/link POST.
        try { await status('uncertain'); } catch { publish({ phase: 'unknown', error: true }); }
      }
    }),
    restart: () => { if (!closed && !state.busy && state.phase === 'code') { clear(); removing = null; publish({ phase: restingPhase(), phoneHint: '', removing: null, error: false }); } },
    dispose: () => { closed = true; clear(); lifetime.abort(); },
  };
}
